import React, { useState } from 'react';
import { XIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
type ModalType = 'privacy' | 'terms' | null;
function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-39">
      <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} data-id="element-40">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-41" />
        <motion.div className="relative bg-[#0f1d32] border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl" initial={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} data-id="element-42">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700" data-id="element-43">
            <h2 className="text-lg font-semibold text-white" data-id="element-44">{title}</h2>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700/50 transition-colors" aria-label="Cerrar" data-id="element-45">
              <XIcon className="w-5 h-5 text-slate-400" data-id="element-46" />
            </button>
          </div>
          <div className="px-6 py-5 overflow-y-auto max-h-[calc(80vh-64px)] text-slate-300 text-sm leading-relaxed space-y-4" data-id="element-47">
            {children}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-[#060e1a] py-8 px-6 border-t border-slate-800" data-id="element-48">
        <div className="max-w-4xl mx-auto text-center" data-id="element-49">
          <p className="text-slate-500 text-sm mb-4" data-id="element-50">
            Este producto no sustituye el consejo médico profesional,
            diagnóstico o tratamiento. Siempre busque el consejo de su médico u
            otro proveedor de salud calificado con cualquier pregunta que pueda
            tener sobre una condición médica.
          </p>
          <div className="flex items-center justify-center gap-4 mb-4" data-id="element-51">
            <button onClick={() => setActiveModal('privacy')} className="text-slate-400 hover:text-teal-400 text-sm transition-colors underline underline-offset-2" data-id="element-52">
              Política de Privacidad
            </button>
            <span className="text-slate-600" data-id="element-53">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-slate-400 hover:text-teal-400 text-sm transition-colors underline underline-offset-2" data-id="element-54">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-slate-600 text-sm" data-id="element-55">
            &copy; {new Date().getFullYear()} Migraña Pro. Todos los derechos
            reservados.
          </p>
        </div>
      </footer>

      <LegalModal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-56">
        <p data-id="element-57">
          <strong className="text-white" data-id="element-58">Última actualización:</strong> Enero
          2025
        </p>
        <p data-id="element-59">
          En Migraña Pro, nos comprometemos a proteger su privacidad. Esta
          política describe cómo recopilamos, usamos y protegemos su información
          personal.
        </p>
        <h3 className="text-white font-semibold pt-2" data-id="element-60">
          1. Información que Recopilamos
        </h3>
        <p data-id="element-61">
          Recopilamos únicamente la información necesaria para procesar su
          compra: nombre, dirección de correo electrónico y datos de pago. No
          almacenamos información de tarjetas de crédito en nuestros servidores.
        </p>
        <h3 className="text-white font-semibold pt-2" data-id="element-62">
          2. Uso de la Información
        </h3>
        <p data-id="element-63">
          Utilizamos su información para: procesar y entregar su compra, enviar
          el producto digital a su correo electrónico, y comunicarnos con usted
          sobre actualizaciones del producto si ha dado su consentimiento.
        </p>
        <h3 className="text-white font-semibold pt-2" data-id="element-64">
          3. Protección de Datos
        </h3>
        <p data-id="element-65">
          Implementamos medidas de seguridad estándar de la industria para
          proteger su información personal. Toda la transmisión de datos se
          realiza a través de conexiones cifradas (SSL/TLS).
        </p>
        <h3 className="text-white font-semibold pt-2" data-id="element-66">
          4. Compartir Información
        </h3>
        <p data-id="element-67">
          No vendemos, intercambiamos ni transferimos su información personal a
          terceros, excepto a proveedores de servicios de pago necesarios para
          completar su transacción.
        </p>
        <h3 className="text-white font-semibold pt-2" data-id="element-68">5. Contacto</h3>
        <p data-id="element-69">
          Si tiene preguntas sobre esta política de privacidad, puede
          contactarnos a través de nuestro correo electrónico de soporte.
        </p>
      </LegalModal>

      <LegalModal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-70">
        <p data-id="element-71">
          <strong className="text-white" data-id="element-72">Última actualización:</strong> Enero
          2025
        </p>
        <p data-id="element-73">
          Al adquirir y utilizar Migraña Pro, usted acepta los siguientes
          términos y condiciones.
        </p>
        <h3 className="text-white font-semibold pt-2" data-id="element-74">1. Producto Digital</h3>
        <p data-id="element-75">
          Migraña Pro es un producto digital en formato PDF que contiene
          información educativa sobre técnicas de manejo del dolor de cabeza. La
          entrega se realiza de forma inmediata tras la confirmación del pago.
        </p>
        <h3 className="text-white font-semibold pt-2" data-id="element-76">
          2. Descargo de Responsabilidad Médica
        </h3>
        <p data-id="element-77">
          Este producto es únicamente con fines informativos y educativos. No
          pretende sustituir el consejo, diagnóstico o tratamiento médico
          profesional. Siempre consulte a un profesional de la salud antes de
          iniciar cualquier programa de salud.
        </p>
        <h3 className="text-white font-semibold pt-2" data-id="element-78">
          3. Propiedad Intelectual
        </h3>
        <p data-id="element-79">
          Todo el contenido de Migraña Pro está protegido por derechos de autor.
          Queda prohibida la reproducción, distribución o reventa del material
          sin autorización expresa por escrito.
        </p>
        <h3 className="text-white font-semibold pt-2" data-id="element-80">
          4. Política de Reembolso
        </h3>
        <p data-id="element-81">
          Debido a la naturaleza digital del producto, no se ofrecen reembolsos
          una vez que el archivo ha sido descargado. Si experimenta problemas
          técnicos con la descarga, nuestro equipo de soporte le asistirá.
        </p>
        <h3 className="text-white font-semibold pt-2" data-id="element-82">
          5. Limitación de Responsabilidad
        </h3>
        <p data-id="element-83">
          Migraña Pro no se hace responsable de los resultados individuales
          obtenidos al aplicar las técnicas descritas en el producto. Los
          resultados pueden variar según cada persona.
        </p>
      </LegalModal>
    </>;
}